import dayjs from 'dayjs';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { VersionRange } from '../core/version-range.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { InvoicingProtocol } from './invoicing/invoicing-protocol.js';
import { PaymentAccountRef } from './payment-account-ref.js';
import { PaymentProvider } from './payment-provider.js';

export enum PaymentAccountStatus {
  Live = 'LIVE',
  Archived = 'ARCHIVED',
}

export class PaymentAccount extends DataModel {
  static readonly TYPE_ID: string = '34d279b5c1c7401bbe4a8811ced405a2';
  static readonly SCHEMA_VERSION: number = 5;

  private constructor(
    public readonly uniqueId: string,
    public readonly name: string,
    public readonly creationDate: dayjs.Dayjs,
    public readonly organizationId: string,
    public readonly marketplaceAccountId: string,
    public readonly marketplaceProvider: PaymentProvider,
    public readonly invoicingProtocol: InvoicingProtocol | null,
    public readonly shouldSplitApplicationFee: boolean,
    public readonly status: PaymentAccountStatus,
  ) {
    super();
  }

  get $typeId() {
    return PaymentAccount.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(3, PaymentAccount.SCHEMA_VERSION);
  }

  static create(args: {
    uniqueId: string,
    name: string,
    creationDate: dayjs.Dayjs,
    organizationId: string,
    marketplaceAccountId: string,
    marketplaceProvider: PaymentProvider,
    invoicingProtocol: InvoicingProtocol | null,
    shouldSplitApplicationFee: boolean,
    status?: PaymentAccountStatus,
  }): PaymentAccount {
    return new PaymentAccount(
        args.uniqueId,
        args.name,
        args.creationDate,
        args.organizationId,
        args.marketplaceAccountId,
        args.marketplaceProvider,
        args.invoicingProtocol,
        args.shouldSplitApplicationFee,
        args.status ?? PaymentAccountStatus.Live,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): PaymentAccount {
    return new PaymentAccount(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'name'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        MapUtils.get<string>(map, 'organizationId'),
        MapUtils.get<string>(map, 'marketplaceAccountId'),
        toEnumFromString(MapUtils.get<string>(map, 'marketplaceProvider'), PaymentProvider),
        context.fromDataOrNull<InvoicingProtocol>(MapUtils.getOrNull<SerializedData>(map, 'invoicingProtocol')),
        MapUtils.get<boolean>(map, 'shouldSplitApplicationFee'),
        toEnumFromString(MapUtils.get<string>(map, '$status'), PaymentAccountStatus),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'name': this.name,
      'creationDate': context.encodeDate(this.creationDate),
      'organizationId': this.organizationId,
      'marketplaceAccountId': this.marketplaceAccountId,
      'marketplaceProvider': fromEnumAsString(this.marketplaceProvider),
      'invoicingProtocol': context.toDataOrNull(this.invoicingProtocol),
      'shouldSplitApplicationFee': this.shouldSplitApplicationFee,
      '$status': fromEnumAsString(this.status),
    };
  }

  clone(): this {
    return new PaymentAccount(
        this.uniqueId,
        this.name,
        this.creationDate.clone(),
        this.organizationId,
        this.marketplaceAccountId,
        this.marketplaceProvider,
        this.invoicingProtocol?.clone() ?? null,
        this.shouldSplitApplicationFee,
        this.status,
    ) as this;
  }

  isSame(other: PaymentAccount): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.name === this.name &&
        other.creationDate === this.creationDate &&
        other.organizationId === this.organizationId &&
        other.marketplaceAccountId === this.marketplaceAccountId &&
        other.marketplaceProvider === this.marketplaceProvider &&
        other.status === this.status &&
        this.shouldSplitApplicationFee === other.shouldSplitApplicationFee &&
        isSameData(other.invoicingProtocol, this.invoicingProtocol);
  }

  copyWith(fields?: {
    invoicingProtocol?: InvoicingProtocol,
    status?: PaymentAccountStatus,
  }): PaymentAccount {
    return new PaymentAccount(
        this.uniqueId,
        this.name,
        this.creationDate.clone(),
        this.organizationId,
        this.marketplaceAccountId,
        this.marketplaceProvider,
        fields?.invoicingProtocol ?? (this.invoicingProtocol?.clone() ?? null),
        this.shouldSplitApplicationFee,
        fields?.status ?? this.status,
    );
  }

  toRef(): PaymentAccountRef {
    return new PaymentAccountRef(this.name, this.uniqueId, this.organizationId);
  }
}

export class PaymentAccountBuilder extends DataModelBuilder<PaymentAccount> {
  build(context: SerializationContext, map: SerializedData): PaymentAccount {
    return PaymentAccount.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(PaymentAccount.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(PaymentAccount.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the [vat] field was added.
    if(version < 2) {
      version = 2;
    }

    // In this version the [invoiceAccountId] and [invoiceProvider] fields were added and [vat] was removed.
    if(version < 3) {
      version = 3;
    }

    // In this version the [shouldSplitApplicationFee] field was added.
    if(version < 4) {
      version = 4;
      map['shouldSplitApplicationFee'] = false;
    }

    // In this version the [status] field was added.
    if(version < 5) {
      version = 5;
      map['$status'] = fromEnumAsString(PaymentAccountStatus.Live);
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
